import { useState, useRef, useEffect } from 'react';
import { fmt, fmtN, isActiveStage } from './plShared';
import { DealModal, DrillModal, useDrill } from './DrillModal';

const DONUT_COLORS = ['#7c3aed', '#2563eb', '#0d9488', '#d97706', '#ea580c', '#dc2626', '#059669'];
const toNumber = value => Number.isFinite(Number(value)) ? Number(value) : 0;
const total = (rows, field = 'amount') => rows.reduce((sum, row) => sum + toNumber(row[field]), 0);
const money = value => fmt(toNumber(value));
const cleanForecast = deal => String(deal.forecast_category || '').trim();
const changePct = (current, previous) => previous ? ((current - previous) / previous) * 100 : 0;
const noValueSuffix = count => count > 0 ? ` (${count} no value deal${count === 1 ? '' : 's'})` : '';

function Sparkline({ values = [], color }) {
  if (!values.length) return null;
  const max = Math.max(...values.map(toNumber).map(Math.abs), 1);
  return (
    <span className="spark" aria-hidden="true">
      {values.map((value, index) => (
        <i
          className="sb"
          key={index}
          style={{ height: `${Math.max(2, Math.round(Math.abs(toNumber(value)) / max * 26))}px`, background: color, flex: 1 }}
        />
      ))}
    </span>
  );
}

function KpiCard({ label, value, delta, deltaClass = '', hint, color, spark, sparkColor, onClick, clickHint = '🔍 Click → drill into deals' }) {
  return (
    <button
      type="button"
      className={`kc ${color}`}
      onClick={onClick}
      style={{ textAlign: 'left', font: 'inherit', width: '100%' }}
    >
      <span className="kl">{label}</span>
      <span className="kv">{value}</span>
      <span className={`kd ${deltaClass}`}>{delta}</span>
      <span className="kh">{hint}</span>
      <span className="click-hint">{clickHint}</span>
      <Sparkline values={spark} color={sparkColor} />
    </button>
  );
}

function Card({ title, sub, children, onClick, hint }) {
  return (
    <section className={`pl-card${onClick ? ' pl-card-clickable' : ''}`} onClick={onClick}>
      <div className="pl-card-header" style={{ display: 'block' }}>
        <div className="pl-card-title">{title}</div>
        {sub && <div className="pl-card-sub">{sub}</div>}
      </div>
      {children}
      {onClick && <div className="ch">{hint || '🔍 Click to drill into deals'}</div>}
    </section>
  );
}

function DonutModeToggle({ mode, onChange }) {
  return (
    <div className="pl-donut-toggle" role="group" aria-label="Pipeline value view">
      {['unweighted', 'weighted'].map(m => (
        <button
          key={m}
          type="button"
          className={`pl-donut-toggle-btn${mode === m ? ' active' : ''}`}
          aria-pressed={mode === m}
          onClick={() => onChange(m)}
        >
          {m === 'unweighted' ? 'UW' : 'Weighted'}
        </button>
      ))}
    </div>
  );
}

function Donut({ items, onItemClick }) {
  const visible = items.filter(item => toNumber(item.value) > 0);
  if (!visible.length) return <div className="pl-chart-empty">No data available.</div>;
  const sum = visible.reduce((value, item) => value + toNumber(item.value), 0) || 1;
  const center = 76;
  const outer = 66;
  const inner = 39;
  let angle = -Math.PI / 2;
  const arcs = visible.map((item, index) => {
    const sweep = toNumber(item.value) / sum * Math.PI * 2;
    const start = angle;
    angle += sweep;
    const point = (radius, radians) => [center + radius * Math.cos(radians), center + radius * Math.sin(radians)];
    const [x1, y1] = point(outer, start);
    const [x2, y2] = point(outer, angle);
    const [x3, y3] = point(inner, start);
    const [x4, y4] = point(inner, angle);
    return {
      item,
      color: DONUT_COLORS[index % DONUT_COLORS.length],
      path: `M${x1} ${y1}A${outer} ${outer} 0 ${sweep > Math.PI ? 1 : 0} 1 ${x2} ${y2}L${x4} ${y4}A${inner} ${inner} 0 ${sweep > Math.PI ? 1 : 0} 0 ${x3} ${y3}Z`,
    };
  });
  return (
    <div className="pl-reference-donut">
      <svg viewBox="0 0 152 152" aria-label="Distribution chart">
        {arcs.map(({ item, color, path }) => (
          <path key={item.label} d={path} fill={color} stroke="#f0f2f5" strokeWidth="2" role="button" tabIndex="0" onClick={event => { event.stopPropagation(); onItemClick(item); }} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.stopPropagation(); onItemClick(item); } }}>
            <title>{`${item.label}: ${money(item.value)}`}</title>
          </path>
        ))}
      </svg>
      <div className="pl-reference-legend">
        {arcs.map(({ item, color }) => (
          <button type="button" key={item.label} onClick={event => { event.stopPropagation(); onItemClick(item); }}>
            <i style={{ background: color }} />
            <span title={item.label}>{item.label}</span>
            <strong>{fmtN(item.value)}</strong>
          </button>
        ))}
      </div>
    </div>
  );
}

function Gauge({ title, week, value, weighted, onClick, targetKind }) {
  const tone = (multiple, weightedValue = false) => {
    if (weightedValue) return multiple >= (targetKind === 'aop' ? 1.5 : 1) ? 'gok' : 'gbad';
    if (multiple >= (targetKind === 'aop' ? 2.5 : 2)) return 'gok';
    return multiple >= 1.5 ? 'gwarn' : 'gbad';
  };
  return (
    <button type="button" className="gauge-box pl-gauge-box" onClick={onClick} style={{ width: '100%' }}>
      <span className="gauge-lbl pl-gauge-lbl">{title}</span>
      <span className={`gauge-v pl-gauge-v ${tone(value)}`}>{value.toFixed(2)}×</span>
      <span className="gauge-sub pl-gauge-sub">Unweighted {week}</span>
      <span className="gauge-sub pl-gauge-sub">Weighted: <b className={tone(weighted, true)}>{weighted.toFixed(2)}×</b></span>
      <span className="click-hint">🔍 Click → gap analysis</span>
    </button>
  );
}

// Formats a raw metric value, shared by every column in the multi-week
// comparison table.
const formatMetricValue = (value, format) => format === 'money' ? money(value) : format === 'multiple' ? `${toNumber(value).toFixed(2)}×` : toNumber(value).toLocaleString();

function ChangeCell({ oldValue, newValue, mode = 'percent' }) {
  if (oldValue === null || oldValue === undefined) return <span>—</span>;
  const old = toNumber(oldValue);
  const current = toNumber(newValue);
  const difference = current - old;
  const rising = difference >= 0;
  let changeText = !old ? (current ? '▲ New' : '—') : `${rising ? '▲ +' : '▼ −'}${Math.abs(changePct(current, old)).toFixed(1)}%`;
  if (mode === 'absolute') changeText = `${rising ? '▲ +' : '▼ −'}${Math.abs(difference).toLocaleString()}`;
  if (mode === 'direction') changeText = rising ? '▲ Improving' : '▼ Declining';
  return <span className={rising ? 'up' : 'dn'}>{changeText}</span>;
}

function CoverageBreakdown({ kind, aop, target, won, active, weighted }) {
  const denominator = kind === 'aop' ? aop : target;
  const name = kind === 'aop' ? 'AOP' : 'Sales Target';
  const percent = value => denominator ? value / denominator * 100 : 0;
  const rows = [['Booked Revenue', won, '#059669'], ['Active Pipeline (Unweighted)', active, '#2563eb'], ['Active Pipeline (Weighted)', weighted, '#d97706']];
  return (
    <div className="pl-coverage-drill">
      <div className="krow k3" style={{ marginBottom: 14 }}>
        <div className="kc kg"><span className="kl">Booked (Won)</span><span className="kv">{money(won)}</span><span className="kd up">{percent(won).toFixed(1)}% of {name}</span></div>
        <div className="kc kb"><span className="kl">Active Pipeline</span><span className="kv">{money(active)}</span><span className="kd fl">{(denominator ? active / denominator : 0).toFixed(2)}× {name}</span></div>
        <div className="kc kr"><span className="kl">Gap to {name}</span><span className="kv">{money(Math.max(0, denominator - won))}</span><span className="kd dn">Remaining to book</span></div>
      </div>
      {rows.map(([label, value, color]) => (
        <div className="pw" key={label}>
          <div className="ph"><span className="pn">{label}</span><span className="pv">{money(value)} — {percent(value).toFixed(1)}%</span></div>
          <div className="pb"><div className="pf" style={{ width: `${Math.min(100, Math.max(0, percent(value)))}%`, background: color }} /></div>
        </div>
      ))}
      <div className="pl-formula-box"><strong>📐 How coverage is calculated</strong>
        <span>Coverage = Active Pipeline ÷ {name}</span><code>{money(active)} ÷ {money(denominator)} = <b>{(denominator ? active / denominator : 0).toFixed(2)}×</b></code>
      </div>
    </div>
  );
}

function Overview({ data, onNavigate, fetchWeekDeals }) {
  const { kpis = {}, stage_dist = [], bu_dist = [], lob_dist = [], weekly_trend = [], executive = {}, deals = [], view_mode = 'unweighted' } = data || {};
  const isWeighted = view_mode === 'weighted';
  const { drill, activeDeal, openDrill, closeDrill, openDeal, closeDeal, drillStage } = useDrill();

  // Each donut below has its own Unweighted/Weighted toggle, independent of
  // the dashboard-wide view toggle, so they can be compared side by side.
  const [stageMode, setStageMode] = useState(view_mode);
  const [forecastMode, setForecastMode] = useState(view_mode);
  const [buMode, setBuMode] = useState(view_mode);
  const [lobMode, setLobMode] = useState(view_mode);
  const pickValue = (item, mode) => mode === 'weighted' ? toNumber(item.weighted) : toNumber(item.amount_uw ?? item.amount);

  const metricDeals = deals.filter(deal => toNumber(deal.amount) > 0);
  const activeDeals = metricDeals.filter(deal => isActiveStage(deal.stage));
  const wonDeals = metricDeals.filter(deal => deal.stage === 'Business Won');
  const lostDeals = metricDeals.filter(deal => deal.stage === 'Business Lost');
  // Forecast-category breakdown is built from whatever values actually
  // appear in the data instead of a fixed Commit/Upside/Not Forecasted
  // list — the CRM has renamed/added categories before (e.g. "Upside"
  // became "Likely" plus a new "Best Case"), and a hardcoded list silently
  // drops any deal whose category doesn't match one of the fixed names,
  // making the breakdown stop summing to the true Active Pipeline total.
  const normalizeForecast = deal => {
    const fc = cleanForecast(deal);
    const lower = fc.toLowerCase();
    if (lower === 'commit') return 'Commit';
    if (lower === '' || lower === 'not forecasted') return 'Not Forecasted';
    return fc;
  };
  const forecastGroups = (() => {
    const byCategory = new Map();
    activeDeals.forEach(deal => {
      const category = normalizeForecast(deal);
      if (!byCategory.has(category)) byCategory.set(category, []);
      byCategory.get(category).push(deal);
    });
    const groups = [...byCategory.entries()].map(([category, rows]) => ({
      category,
      deals: rows,
      uw: total(rows),
      weighted: total(rows, 'weighted'),
    }));
    // Commit always leads (highest-confidence, familiar position), Not
    // Forecasted always trails (least-qualified); anything else sorts by
    // dollar value in between, so a newly-added category lands somewhere
    // sensible without needing a hand-maintained position.
    const rank = category => category === 'Commit' ? -1 : category === 'Not Forecasted' ? 1 : 0;
    return groups.sort((a, b) => rank(a.category) - rank(b.category) || b.uw - a.uw);
  })();

  const active = toNumber(kpis.active_pipeline ?? total(activeDeals));
  const activeUw = toNumber(kpis.active_pipeline_uw ?? total(activeDeals));
  const weighted = toNumber(kpis.weighted_pipeline ?? total(activeDeals, 'weighted'));
  const won = toNumber(kpis.won_ytd ?? total(wonDeals));
  // Closed deals always show their actual dollar amount, regardless of the
  // Unweighted/Weighted toggle — "weighted" (probability × amount) is
  // meaningless once a deal is already won or lost, and Lost specifically
  // carries a 0% stage probability, which would zero out the whole card.
  const wonUw = toNumber(kpis.won_ytd_uw ?? total(wonDeals, 'amount'));
  const lostUw = toNumber(kpis.lost_ytd_uw ?? total(lostDeals, 'amount'));
  const aop = toNumber(kpis.aop);
  const target = toNumber(kpis.sales_target);

  const latestTrend = weekly_trend[weekly_trend.length - 1] || {};
  const selectedWeek = executive.selected_week_short || (data?.selected_week ? `W${String(data.selected_week).replace(/\D/g, '')}` : `W${latestTrend.week_num || ''}`);
  const baselineWeek = executive.baseline_week_short || `W${weekly_trend[0]?.week_num || 1}`;
  const period = executive.period_comparison || {};
  const baseline = period.baseline || weekly_trend[0] || {};
  const activeDelta = toNumber(executive.active_change_vs_baseline ?? changePct(active, baseline.active));

  // Period Comparison landmark weeks: W1, then every 10th week (W10, W20,
  // W30…), always including the latest available week.
  const cmpMaxWeekNum = weekly_trend[weekly_trend.length - 1]?.week_num || 1;
  const cmpLandmarkNums = [1];
  for (let n = 10; n <= cmpMaxWeekNum; n += 10) cmpLandmarkNums.push(n);
  const cmpWeekByNum = Object.fromEntries(weekly_trend.map(row => [row.week_num, row]));
  const cmpLandmarkWeeks = cmpLandmarkNums.map(n => cmpWeekByNum[n]).filter(Boolean);
  if (latestTrend.week && !cmpLandmarkWeeks.some(row => row.week_num === latestTrend.week_num)) cmpLandmarkWeeks.push(latestTrend);
  // Period Comparison week filter: pick any number of weeks from a
  // dropdown (not just one landmark week) — each selected week gets its
  // own column in the table, same multi-select pattern as the Full
  // Benchmark and FP&A scorecards.
  const [cmpWeeks, setCmpWeeks] = useState(() => cmpLandmarkWeeks.map(row => row.week));
  const cmpSelectedRows = cmpWeeks
    .map(w => weekly_trend.find(row => row.week === w))
    .filter(Boolean)
    .sort((a, b) => a.week_num - b.week_num);
  const [cmpDropdownOpen, setCmpDropdownOpen] = useState(false);
  const cmpDropdownRef = useRef(null);
  useEffect(() => {
    if (!cmpDropdownOpen) return;
    const close = event => { if (cmpDropdownRef.current && !cmpDropdownRef.current.contains(event.target)) setCmpDropdownOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [cmpDropdownOpen]);
  const toggleCmpWeek = week => {
    setCmpWeeks(current => current.includes(week) ? current.filter(w => w !== week) : [...current, week]);
  };

  // Recomputes every Period Comparison row's ratio from a single
  // weekly_trend snapshot row, so any selected week gets its own column.
  const metricsForRow = row => {
    const activeUwV = toNumber(row.active_uw);
    const weightedV = toNumber(row.weighted);
    const countV = toNumber(row.count);
    return {
      activeUwV, weightedV,
      wonV: toNumber(row.won),
      lostV: toNumber(row.lost),
      countV,
      noValueV: Math.max(0, toNumber(row.active_count_all) - countV),
      avgDealV: countV ? activeUwV / countV : 0,
      covTargetV: target ? activeUwV / target : 0,
      covAopV: aop ? activeUwV / aop : 0,
      wCovTargetV: target ? weightedV / target : 0,
      wCovAopV: aop ? weightedV / aop : 0,
    };
  };
  const curNoValue = Math.max(0, toNumber(kpis.active_novalue_deals));
  const curAvgDeal = toNumber(kpis.avg_deal_size);
  const curWeekRow = weekly_trend.find(row => row.week === data?.selected_week) || latestTrend;

  const openWeekMetric = async (weekRow, weekShort, metricKey, label, totalField = 'amount') => {
    const weekLabel = weekRow.week;
    if (!weekLabel || !fetchWeekDeals) return;
    openDrill(`${weekShort} — ${label}`, 'Loading…', null, <div className="pl-chart-empty">Loading {weekShort} deals…</div>);
    try {
      const raw = await fetchWeekDeals(weekLabel, { includeZero: metricKey === 'novalue' });
      let rows;
      if (metricKey === 'active') rows = raw.filter(deal => isActiveStage(deal.stage) && toNumber(deal.amount) > 0);
      else if (metricKey === 'novalue') rows = raw.filter(deal => isActiveStage(deal.stage) && toNumber(deal.amount) <= 0);
      else if (metricKey === 'won') rows = raw.filter(deal => deal.stage === 'Business Won');
      else if (metricKey === 'lost') rows = raw.filter(deal => deal.stage === 'Business Lost');
      else rows = raw;
      openDrill(`${weekShort} — ${label}`, `${rows.length} deals · sorted by ${totalField === 'weighted' ? 'weighted value' : 'amount'}`, rows, null, { totalField });
    } catch (err) {
      openDrill(`${weekShort} — ${label}`, '', null, <div className="pl-chart-empty">Could not load {weekShort}&rsquo;s deals.</div>);
    }
  };

  const openAvgDealFormula = (weekRow, weekShort) => {
    const activeVal = toNumber(weekRow.active_uw);
    const cnt = toNumber(weekRow.count);
    const avg = cnt ? activeVal / cnt : 0;
    const content = (
      <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: 14 }}>
        <div style={{ fontSize: 11, color: 'var(--sub)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>Formula</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--cyan)' }}>Active Pipeline (UW) ÷ Active Deal Count</div>
        <div style={{ marginTop: 10, fontFamily: 'monospace', fontSize: 12 }}>{money(activeVal)} ÷ {cnt} = <strong>{money(avg)}</strong></div>
      </div>
    );
    openDrill(`${weekShort} — Avg Deal Size`, 'How this is calculated', null, content);
  };

  const openCoverageFormula = (weekRow, weekShort, kind) => {
    const weightedKind = kind.startsWith('weighted');
    const aopKind = kind.endsWith('aop');
    const denomName = aopKind ? 'AOP' : 'Sales Target';
    const denom = aopKind ? aop : target;
    const numerator = weightedKind ? toNumber(weekRow.weighted) : toNumber(weekRow.active_uw);
    const numeratorLabel = weightedKind ? 'Weighted Pipeline' : 'Active Pipeline (UW)';
    const ratio = denom ? numerator / denom : 0;
    const content = (
      <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: 14 }}>
        <div style={{ fontSize: 11, color: 'var(--sub)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>Formula</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--cyan)' }}>{numeratorLabel} ÷ {denomName}</div>
        <div style={{ marginTop: 10, fontFamily: 'monospace', fontSize: 12 }}>{money(numerator)} ÷ {money(denom)} = <strong>{ratio.toFixed(2)}×</strong></div>
      </div>
    );
    openDrill(`${weekShort} — ${weightedKind ? 'Weighted ' : ''}Coverage vs ${denomName}`, 'How this is calculated', null, content);
  };
  const commitBaseline = toNumber(weekly_trend[0]?.commit ?? baseline.commit);
  const coverageAop = aop ? active / aop : 0;
  const coverageTarget = target ? active / target : 0;
  const weightedAop = aop ? weighted / aop : 0;
  const weightedTarget = target ? weighted / target : 0;
  const spark = key => weekly_trend.map(week => week[key]);
  const openActive = () => openDrill('Active Pipeline — All Active Deals', `Excludes Business Won and Business Shrinkage · ${activeDeals.length} deals · sorted by amount`, activeDeals);
  const openActiveWeighted = () => openDrill('Active Pipeline (Weighted) — All Active Deals', `Weighted Total: ${money(weighted)} · UW Total: ${money(activeUw)} · ${activeDeals.length} deals · sorted by weighted value`, activeDeals, null, { totalField: 'weighted' });
  const openWon = () => openDrill(`Won Deals — ${wonDeals.length} Deals`, 'All won deals · Click any row for full detail', wonDeals);
  const openForecastGroup = group => openDrill(`${group.category} Pipeline — ${group.deals.length} Deals`, `Forecast category: ${group.category} · Click any row for full detail`, group.deals);
  const openLost = () => openDrill(`Shrinkage Pipeline — ${lostDeals.length} Deals`, 'All deals shrinkage YTD · sorted by amount · Click any row for full detail', lostDeals);
  const openCoverage = kind => {
    const denominator = kind === 'aop' ? aop : target;
    const name = kind === 'aop' ? 'AOP' : 'Sales Target';
    openDrill(`${name} Coverage Analysis`, `${money(denominator)} ${name} vs current pipeline`, null, <CoverageBreakdown kind={kind} aop={aop} target={target} won={won} active={active} weighted={weighted} />);
  };

  // Row config for the Period Comparison table: one entry per metric, each
  // knowing how to read its own value off a weekly_trend row and how to
  // drill into either a selected week's deals or the current week's.
  const compareMetrics = [
    { key: 'active', label: 'Pipeline UW', format: 'money', value: r => metricsForRow(r).activeUwV, current: activeUw, onWeekClick: (row, short) => () => openWeekMetric(row, short, 'active', 'Active Pipeline (With Value)'), onCurrentClick: openActive },
    { key: 'weightedpipe', label: 'Pipeline Weighted', format: 'money', value: r => metricsForRow(r).weightedV, current: weighted, onWeekClick: (row, short) => () => openWeekMetric(row, short, 'active', 'Active Pipeline (Weighted)', 'weighted'), onCurrentClick: openActiveWeighted },
    { key: 'countv', label: 'Active Deals (With Value)', format: 'absolute', change: 'absolute', value: r => metricsForRow(r).countV, current: toNumber(kpis.active_deals ?? activeDeals.length), onWeekClick: (row, short) => () => openWeekMetric(row, short, 'active', 'Active Deals (With Value)'), onCurrentClick: openActive },
    { key: 'novalue', label: 'Active Deals (No Value)', format: 'absolute', change: 'absolute', value: r => metricsForRow(r).noValueV, current: curNoValue, onWeekClick: (row, short) => () => openWeekMetric(row, short, 'novalue', 'Active Deals (No Value)'), onCurrentClick: () => openWeekMetric(curWeekRow, selectedWeek, 'novalue', 'Active Deals (No Value)') },
    { key: 'won', label: 'Won YTD', format: 'money', value: r => metricsForRow(r).wonV, current: wonUw, onWeekClick: (row, short) => () => openWeekMetric(row, short, 'won', 'Won Deals'), onCurrentClick: openWon },
    { key: 'lost', label: 'Shrinkage YTD', format: 'money', value: r => metricsForRow(r).lostV, current: lostUw, onWeekClick: (row, short) => () => openWeekMetric(row, short, 'lost', 'Shrinkage Deals'), onCurrentClick: openLost },
    { key: 'avgdeal', label: 'Avg Deal Size', format: 'money', value: r => metricsForRow(r).avgDealV, current: curAvgDeal, onWeekClick: (row, short) => () => openAvgDealFormula(row, short), onCurrentClick: () => openAvgDealFormula(curWeekRow, selectedWeek) },
    { key: 'covtarget', label: 'Cov vs Target', format: 'multiple', change: 'direction', value: r => metricsForRow(r).covTargetV, current: coverageTarget, onWeekClick: (row, short) => () => openCoverageFormula(row, short, 'target'), onCurrentClick: () => openCoverageFormula(curWeekRow, selectedWeek, 'target') },
    { key: 'wcovtarget', label: 'Weighted Cov (Target)', format: 'multiple', change: 'direction', value: r => metricsForRow(r).wCovTargetV, current: weightedTarget, onWeekClick: (row, short) => () => openCoverageFormula(row, short, 'weighted_target'), onCurrentClick: () => openCoverageFormula(curWeekRow, selectedWeek, 'weighted_target') },
    { key: 'covaop', label: 'Cov vs AOP', format: 'multiple', change: 'direction', value: r => metricsForRow(r).covAopV, current: coverageAop, onWeekClick: (row, short) => () => openCoverageFormula(row, short, 'aop'), onCurrentClick: () => openCoverageFormula(curWeekRow, selectedWeek, 'aop') },
    { key: 'wcovaop', label: 'Weighted Cov (AOP)', format: 'multiple', change: 'direction', value: r => metricsForRow(r).wCovAopV, current: weightedAop, onWeekClick: (row, short) => () => openCoverageFormula(row, short, 'weighted_aop'), onCurrentClick: () => openCoverageFormula(curWeekRow, selectedWeek, 'weighted_aop') },
  ];

  const stageItems = stage_dist.map(item => ({ label: item.stage, value: pickValue(item, stageMode), key: item.stage }));
  // Built from the same forecastGroups used by the breakdown cards above,
  // instead of the backend's forecast_dist — that source bucketed a blank
  // category as "None" separately from the literal "Not forecasted" string,
  // splitting what should be one bucket into two. Sharing forecastGroups
  // keeps this donut and the breakdown cards always in agreement.
  const forecastItems = forecastGroups.map(group => ({ label: group.category, value: forecastMode === 'weighted' ? group.weighted : group.uw }));
  const buItems = bu_dist.map(item => ({ label: item.bu || 'Unknown', value: pickValue(item, buMode) }));
  const lobItems = lob_dist.map(item => ({ label: item.lob || 'Unknown', value: pickValue(item, lobMode) }));
  // The shared deal-table drill always totals the raw $ amount column, so a
  // segment clicked while a donut is in Weighted mode would otherwise show
  // the UW total with no indication the numbers don't match what was on
  // screen — spell out both totals in the sub-header instead.
  const totalsSub = (rows, mode, note) => {
    const uwTotal = total(rows);
    const wTotal = total(rows, 'weighted');
    const totalsText = mode === 'weighted'
      ? `Weighted Total: ${money(wTotal)} · UW Total: ${money(uwTotal)}`
      : `UW Total: ${money(uwTotal)}`;
    return `${note} · ${totalsText} · Click any row for full detail`;
  };

  const drillStageDeals = (stage, title = stage) => {
    const rows = activeDeals.filter(deal => deal.stage === stage);
    openDrill(`${title} — ${rows.length} Deals`, totalsSub(rows, stageMode, `All deals in ${stage} stage`), rows, null, { totalField: stageMode === 'weighted' ? 'weighted' : 'amount' });
  };

  return (
    <div className="pl-reference pl-reference-executive">
      {!isWeighted && (
        <div className="kpi-group kpi-group-purple">
          <div className="kpi-group-title">🟣 Active Pipeline — Unweighted Breakdown</div>
          <div className="krow">
            <KpiCard label={`Active Pipeline ${selectedWeek}`} value={money(activeUw)} delta={`${activeDelta >= 0 ? '▲' : '▼ −'} ${Math.abs(activeDelta).toFixed(1)}% vs ${baselineWeek}`} hint={`${toNumber(kpis.active_deals ?? activeDeals.length)} active deals With Value${noValueSuffix(toNumber(kpis.active_novalue_deals))}`} color="kc-parent-purple" spark={spark('active')} sparkColor="#ffffff" onClick={openActive} />
            {forecastGroups.map(group => (
              <KpiCard
                key={group.category}
                label={group.category}
                value={money(group.uw)}
                delta={group.category === 'Commit' && commitBaseline ? `▼ from ${money(commitBaseline)} ${baselineWeek}` : group.category === 'Not Forecasted' ? 'needs qualification' : 'active pipeline'}
                hint={`${group.deals.length} deal${group.deals.length === 1 ? '' : 's'} With Value`}
                color="kc-child-purple"
                onClick={() => openForecastGroup(group)}
              />
            ))}
          </div>
        </div>
      )}

      {isWeighted && (
        <div className="kpi-group kpi-group-pink">
          <div className="kpi-group-title">💗 Active Pipeline — Weighted Breakdown</div>
          <div className="krow">
            <KpiCard label={`Active Pipeline ${selectedWeek} (Weighted)`} value={money(weighted)} delta={`${weightedAop.toFixed(2)}× AOP · ${weightedTarget.toFixed(2)}× Target`} hint={`Excel Weighted column · ${selectedWeek}`} color="kc-parent-pink" onClick={() => openDrill(`Weighted Pipeline — ${activeDeals.filter(deal => toNumber(deal.weighted) > 0).length} Deals`, `Sorted by Weighted value (Excel) · Weighted Total: ${money(weighted)} · UW Total: ${money(activeUw)}`, activeDeals.filter(deal => toNumber(deal.weighted) > 0), null, { totalField: 'weighted' })} />
            {forecastGroups.map(group => (
              <KpiCard
                key={group.category}
                label={group.category}
                value={money(group.weighted)}
                delta="weighted value"
                hint={`${group.deals.length} deal${group.deals.length === 1 ? '' : 's'} With Value`}
                color="kc-child-pink"
                onClick={() => openForecastGroup(group)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="kpi-group kpi-group-neutral">
        <div className="kpi-group-title">Closed Deals (always actual $ — not weighted by probability)</div>
        <div className="krow" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          <KpiCard label="Won YTD" value={money(wonUw)} delta={`▲ ${toNumber(kpis.won_deals ?? wonDeals.length)} deals closed`} deltaClass="up" hint={`${toNumber(kpis.won_deals ?? wonDeals.length)} deals With Value closed YTD${noValueSuffix(toNumber(kpis.won_novalue_deals))}`} color="kg" spark={spark('won')} sparkColor="#059669" onClick={openWon} />
          <KpiCard label="Shrinkage Pipeline YTD" value={money(lostUw)} delta={`${toNumber(kpis.lost_deals ?? lostDeals.length)} deals With Value shrinkage${noValueSuffix(toNumber(kpis.lost_novalue_deals))}`} deltaClass="dn" hint="Accelerating loss rate" color="kr" spark={spark('lost')} sparkColor="#dc2626" onClick={() => openDrill(`Shrinkage Pipeline — ${lostDeals.length} Deals`, 'All deals shrinkage YTD · sorted by amount · Click any row for full detail', lostDeals)} />
        </div>
      </div>

      <div className="g1">
        <Card title="Coverage Gauges — Click any gauge to drill" sub="Unweighted and weighted pipeline coverage">
          <div className="gauge-row pl-gauge-row"><Gauge title={`vs Sales Target (${money(target)})`} week={selectedWeek} value={coverageTarget} weighted={weightedTarget} targetKind="target" onClick={() => openCoverage('target')} /><Gauge title={`vs AOP (${money(aop)})`} week={selectedWeek} value={coverageAop} weighted={weightedAop} targetKind="aop" onClick={() => openCoverage('aop')} /></div>
          <div className="dv" />
          <div className="pl-card-title" style={{ marginBottom: 8 }}>Period Comparison — Click a cell to drill into that week</div>

          <div className="sc-week-filter" ref={cmpDropdownRef}>
            <span className="sc-week-filter-label">📅 Filter by Week</span>
            <div className="sc-week-dropdown">
              <button type="button" className="sc-week-dropdown-btn" onClick={() => setCmpDropdownOpen(open => !open)} aria-haspopup="listbox" aria-expanded={cmpDropdownOpen}>
                {cmpWeeks.length ? `${cmpWeeks.length} week${cmpWeeks.length === 1 ? '' : 's'} selected` : 'Select weeks'}
                <span className="sc-week-dropdown-arrow">{cmpDropdownOpen ? '▲' : '▼'}</span>
              </button>
              {cmpDropdownOpen && (
                <div className="sc-week-dropdown-panel" role="listbox" aria-multiselectable="true">
                  {weekly_trend.map(row => (
                    <label className="sc-week-dropdown-option" key={row.week}>
                      <input type="checkbox" checked={cmpWeeks.includes(row.week)} onChange={() => toggleCmpWeek(row.week)} />
                      W{row.week_num}{row.week_num === latestTrend.week_num ? ' (Latest)' : ''}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <span className="sc-week-filter-hint">Click to open, check the weeks you want to compare</span>
          </div>

          <div className="pl-twrap" style={{ marginTop: 8 }}><table><thead><tr>
            <th>Metric</th>
            {cmpSelectedRows.map(row => <th key={row.week}>W{row.week_num}</th>)}
            <th>{selectedWeek}</th><th>Change</th>
          </tr></thead><tbody>
            {compareMetrics.map(metric => {
              const earliestRow = cmpSelectedRows[0];
              return (
                <tr key={metric.key}>
                  <td>{metric.label}</td>
                  {cmpSelectedRows.map(row => (
                    <td
                      className="td2 pl-bm-week-cell"
                      key={row.week}
                      title={`Click for ${row.week} detail`}
                      onClick={metric.onWeekClick(row, `W${row.week_num}`)}
                    >
                      {formatMetricValue(metric.value(row), metric.format)}
                    </td>
                  ))}
                  <td className="td2 pl-click-row" style={{ cursor: 'pointer' }} onClick={metric.onCurrentClick}>{formatMetricValue(metric.current, metric.format)}</td>
                  <td><ChangeCell oldValue={earliestRow ? metric.value(earliestRow) : null} newValue={metric.current} mode={metric.change} /></td>
                </tr>
              );
            })}
          </tbody></table></div>
        </Card>
      </div>

      <div className="g4">
        <Card title={`Stage Distribution ${selectedWeek} ↗ Click segment`} sub="Click any segment → deals in that stage">
          <DonutModeToggle mode={stageMode} onChange={setStageMode} />
          <Donut items={stageItems} onItemClick={item => drillStageDeals(item.key)} />
        </Card>
        <Card title={`Forecast Mix ${selectedWeek} ↗ Click segment`} sub="Click any segment → deals in that category">
          <DonutModeToggle mode={forecastMode} onChange={setForecastMode} />
          <Donut items={forecastItems} onItemClick={item => { const rows = activeDeals.filter(deal => normalizeForecast(deal) === item.label); openDrill(`${item.label} Pipeline — ${rows.length} Deals`, totalsSub(rows, forecastMode, `All ${item.label} forecast deals`), rows, null, { totalField: forecastMode === 'weighted' ? 'weighted' : 'amount' }); }} />
        </Card>
        <Card title={`BU Split ${selectedWeek} ↗ Click segment`} sub="Click any segment → deals in that BU">
          <DonutModeToggle mode={buMode} onChange={setBuMode} />
          <Donut items={buItems} onItemClick={item => { const rows = activeDeals.filter(deal => (deal.bu || 'Unknown') === item.label); openDrill(`${item.label} — ${rows.length} Deals`, totalsSub(rows, buMode, `All ${item.label} active deals`), rows, null, { totalField: buMode === 'weighted' ? 'weighted' : 'amount' }); }} />
        </Card>
        <Card title={`LOB Split ${selectedWeek} ↗ Click segment`} sub="Click any segment → deals in that LOB">
          <DonutModeToggle mode={lobMode} onChange={setLobMode} />
          <Donut items={lobItems} onItemClick={item => { const rows = activeDeals.filter(deal => (deal.lob || 'Unknown') === item.label); openDrill(`${item.label} — ${rows.length} Deals`, totalsSub(rows, lobMode, `All ${item.label} active deals`), rows, null, { totalField: lobMode === 'weighted' ? 'weighted' : 'amount' }); }} />
        </Card>
      </div>

      {drill && <DrillModal title={drill.title} sub={drill.sub} deals={drill.deals} totalField={drill.totalField} totalLabel={drill.totalLabel} onClose={closeDrill} onDealClick={openDeal}>{drill.children}</DrillModal>}
      {activeDeal && <DealModal deal={activeDeal} onClose={closeDeal} onDrillStage={stage => drillStage(stage, metricDeals)} />}
    </div>
  );
}

export { Overview };
export default Overview;
